/**
 * Simulation Clone Editor
 *
 * Clone a completed simulation, edit a specific turn, and re-run from that point.
 * This is the core "branching" feature for prompt iteration.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  PlayArrow as RunIcon,
  ContentCopy as CloneIcon,
  History as HistoryIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import {
  getAdvancedSimulationRun,
  cloneAndRerunAdvancedSimulation,
  getCloneLineage,
  listAdvancedSimulationRuns
} from '../../../services/advancedSimulationApi';

function SimulationCloneEditor({ token, initialRunId, onCloneCompleted }) {
  const [sourceRun, setSourceRun] = useState(null);
  const [lineage, setLineage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [recentRuns, setRecentRuns] = useState([]);

  // Edit state
  const [selectedTurnIndex, setSelectedTurnIndex] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Result state
  const [cloneResult, setCloneResult] = useState(null);

  useEffect(() => {
    if (initialRunId) {
      loadRun(initialRunId);
    } else {
      loadRecentRuns();
    }
  }, [initialRunId, token]);

  const loadRecentRuns = async () => {
    setLoading(true);
    try {
      const result = await listAdvancedSimulationRuns({
        status: 'completed',
        limit: 10
      }, token);
      setRecentRuns(result.runs || []);
    } catch (err) {
      console.error('Failed to load recent runs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRun = async (runId) => {
    setLoading(true);
    setError(null);

    try {
      const [run, lineageData] = await Promise.all([
        getAdvancedSimulationRun(runId, token),
        getCloneLineage(runId, token).catch(() => ({ lineage: [] }))
      ]);

      setSourceRun(run);
      setLineage(lineageData.lineage || []);
    } catch (err) {
      setError('Failed to load simulation run');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTurn = (turnIndex) => {
    if (!sourceRun?.turns) return;

    const turn = sourceRun.turns[turnIndex];
    setSelectedTurnIndex(turnIndex);
    setEditedContent(turn.content);
    setEditDialogOpen(true);
  };

  const handleCloneAndRerun = async () => {
    if (selectedTurnIndex === null || !editedContent.trim()) {
      setError('Please select a turn and provide edited content');
      return;
    }

    setCloning(true);
    setError(null);

    try {
      const result = await cloneAndRerunAdvancedSimulation(
        sourceRun._id,
        selectedTurnIndex,
        editedContent.trim(),
        token
      );

      setCloneResult(result);
      setEditDialogOpen(false);

      // Load the new run
      if (result._id) {
        await loadRun(result._id);
        onCloneCompleted?.(result._id);
      }
    } catch (err) {
      setError(err.error || 'Failed to clone and re-run simulation');
    } finally {
      setCloning(false);
    }
  };

  const handleReset = () => {
    setSourceRun(null);
    setLineage([]);
    setSelectedTurnIndex(null);
    setEditedContent('');
    setCloneResult(null);
    loadRecentRuns();
  };

  if (loading && !sourceRun) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Run selection view
  if (!sourceRun) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Clone Studio
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select a completed simulation to clone, edit a conversation turn, and re-run from that point.
        </Typography>

        {recentRuns.length === 0 ? (
          <Alert severity="info">
            No completed simulations found. Run a simulation first to use the Clone Studio.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {recentRuns.map((run) => (
              <Grid item xs={12} md={6} key={run._id}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                  }}
                  onClick={() => loadRun(run._id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {run.runId?.substring(0, 12)}...
                      </Typography>
                      <Chip
                        label={run.runType}
                        size="small"
                        variant="outlined"
                        color={run.runType === 'cloned' ? 'secondary' : 'default'}
                      />
                    </Box>
                    <Typography variant="body2">
                      {run.configSnapshot?.clientPersonaKey} × {run.configSnapshot?.therapistPersonaKey}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {run.turns?.length || 0} turns | Quality: {run.metrics?.qualityScore?.toFixed(2) || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Clone Studio
        </Typography>
        <Button
          variant="outlined"
          onClick={handleReset}
          size="small"
        >
          Select Different Run
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {cloneResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCloneResult(null)}>
          Clone created successfully! The new simulation is now running.
        </Alert>
      )}

      {/* Source Run Info */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">Run ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {sourceRun.runId?.substring(0, 16)}...
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography variant="body2">
                <Chip
                  label={sourceRun.runType}
                  size="small"
                  color={sourceRun.runType === 'cloned' ? 'secondary' : 'default'}
                />
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Client</Typography>
              <Typography variant="body2">{sourceRun.configSnapshot?.clientPersonaKey}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Therapist</Typography>
              <Typography variant="body2">{sourceRun.configSnapshot?.therapistPersonaKey}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Metrics</Typography>
              <Typography variant="body2">
                {sourceRun.turns?.length} turns | Q: {sourceRun.metrics?.qualityScore?.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lineage */}
      {lineage.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon color="primary" />
              <Typography variant="subtitle2">Clone Lineage</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {lineage.map((ancestor, idx) => (
                <Chip
                  key={ancestor._id}
                  label={`${idx === 0 ? 'Original' : `Clone ${idx}`}: ${ancestor.runId?.substring(0, 8)}`}
                  size="small"
                  variant={ancestor._id === sourceRun._id ? 'filled' : 'outlined'}
                  color={ancestor._id === sourceRun._id ? 'primary' : 'default'}
                  onClick={() => ancestor._id !== sourceRun._id && loadRun(ancestor._id)}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Click on any conversation turn below to select it as the breakpoint. You can then edit the message
        and re-run the simulation from that point with the edited content.
      </Alert>

      {/* Conversation Turns */}
      <Typography variant="subtitle1" gutterBottom>
        Conversation ({sourceRun.turns?.length || 0} turns)
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, maxHeight: 500, overflowY: 'auto' }}>
        {sourceRun.turns?.map((turn, idx) => (
          <Box
            key={idx}
            onClick={() => handleSelectTurn(idx)}
            sx={{
              p: 2,
              mb: 1,
              bgcolor: turn.actor === 'client' ? 'grey.100' : 'primary.50',
              borderRadius: 1,
              cursor: 'pointer',
              border: selectedTurnIndex === idx ? '2px solid' : '1px solid transparent',
              borderColor: selectedTurnIndex === idx ? 'primary.main' : 'transparent',
              '&:hover': {
                borderColor: 'primary.light',
                bgcolor: turn.actor === 'client' ? 'grey.200' : 'primary.100'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Turn {turn.turnIndex} - {turn.actor.toUpperCase()}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {turn.humanScore && (
                  <Chip
                    label={`Score: ${turn.humanScore.score}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
                <IconButton size="small" title="Edit and re-run from here">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Typography variant="body2">{turn.content}</Typography>
            {turn.metadata && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {turn.metadata.latencyMs}ms | {turn.metadata.tokens?.total} tokens |
                Model: {turn.metadata.model}
              </Typography>
            )}
          </Box>
        ))}
      </Paper>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Turn {selectedTurnIndex} and Re-run
        </DialogTitle>
        <DialogContent>
          {selectedTurnIndex !== null && sourceRun?.turns?.[selectedTurnIndex] && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                The simulation will be cloned up to turn {selectedTurnIndex}, then your edited message
                will be used and the simulation will continue from there.
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                Original Message ({sourceRun.turns[selectedTurnIndex].actor}):
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  {sourceRun.turns[selectedTurnIndex].content}
                </Typography>
              </Paper>

              <Typography variant="subtitle2" gutterBottom>
                Edited Message:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                placeholder="Enter the edited message..."
                disabled={cloning}
              />

              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark">
                  <strong>What will happen:</strong>
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  1. Turns 0-{selectedTurnIndex - 1} will be copied exactly
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  2. Turn {selectedTurnIndex} will use your edited message
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  3. The simulation will continue with AI responses
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={cloning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCloneAndRerun}
            disabled={cloning || !editedContent.trim()}
            startIcon={cloning ? <CircularProgress size={20} /> : <RunIcon />}
          >
            {cloning ? 'Cloning...' : 'Clone & Re-run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SimulationCloneEditor;
